import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useAuthContext } from '../../../context/AuthContext'
import { ProductRecognitionDetectionContent } from '../../../pages/store-details/components/ProductRecognitionDetectionContent'
import type { ProductRecognitionBBox, ProductRecognitionPolygon, VizSimCandidate, VizSimReferenceCrop } from '../../../types'
import { MetricsSummary } from '../../components/common/MetricsSummary'
import { RunTypeBadge } from '../../components/common/RunTypeBadge'
import { EvalApiService } from '../../services/EvalApiService'
import { EvalGtSample, EvalRun, RunInferenceDetail } from '../../types'
import { fmtIso } from '../../utils'


// ── Layer definitions ─────────────────────────────────────────────────────────

interface LayerDef {
  key: string
  label: string
  fill: string
  stroke: string
  type: 'polygon' | 'bbox'
}

const LAYERS: LayerDef[] = [
  { key: 'product_polygons',  label: 'Products',        fill: 'rgba(56,189,248,0.18)',  stroke: 'rgba(56,189,248,0.95)',  type: 'polygon' },
  { key: 'carton_polygons',   label: 'Cartons',         fill: 'rgba(251,146,60,0.18)',  stroke: 'rgba(251,146,60,0.95)',  type: 'polygon' },
  { key: 'mod_polygons',      label: 'Mods',            fill: 'rgba(74,222,128,0.18)',  stroke: 'rgba(74,222,128,0.95)',  type: 'polygon' },
  { key: 'shelf_boxes',       label: 'Shelves',         fill: 'rgba(250,204,21,0.18)',  stroke: 'rgba(250,204,21,0.95)',  type: 'bbox'    },
  { key: 'template_boxes',    label: 'Template Boxes',  fill: 'rgba(192,132,252,0.18)', stroke: 'rgba(192,132,252,0.95)', type: 'bbox'    },
  { key: 'price_tag_boxes',   label: 'Price Tags',      fill: 'rgba(248,113,113,0.18)', stroke: 'rgba(248,113,113,0.95)', type: 'bbox'    },
]

// PR runs only show products and cartons, coloured by barcode agreement.
const PR_LAYERS = LAYERS.filter((l) => l.key === 'product_polygons' || l.key === 'carton_polygons')

// template_box_shape_type tells us which of the two template box lists is active.
// 'template_box_up' → template_boxes_up; anything else → template_boxes.
function resolveLayerKey(key: string, inference: Record<string, unknown> | null): string {
  if (key === 'template_boxes') {
    const shapeType = inference?.template_box_shape_type as string | undefined
    return shapeType === 'template_box_up' ? 'template_boxes_up' : 'template_boxes'
  }
  return key
}

function shapeCount(inference: Record<string, unknown> | null, key: string): number {
  if (!inference) return 0
  const arr = inference[resolveLayerKey(key, inference)]
  return Array.isArray(arr) ? arr.length : 0
}


// ── Canvas drawing ────────────────────────────────────────────────────────────

const TP_COLORS      = { fill: 'rgba(59,130,246,0.15)',  stroke: 'rgba(59,130,246,0.9)'  }
const FP_COLORS      = { fill: 'rgba(234,179,8,0.15)',   stroke: 'rgba(234,179,8,0.9)'   }
const FN_COLORS      = { fill: 'rgba(239,68,68,0.15)',   stroke: 'rgba(239,68,68,0.9)'   }
const CORRECT_COLORS = { fill: 'rgba(59,130,246,0.15)',  stroke: 'rgba(59,130,246,0.9)'  }
const WRONG_COLORS   = { fill: 'rgba(239,68,68,0.15)',   stroke: 'rgba(239,68,68,0.9)'   }

function getBarcode(shape: Record<string, unknown>): string | null {
  const cands = shape.viz_sim_candidates as Array<Record<string, unknown>> | undefined
  return (cands?.[0]?.normalized_barcode as string) ?? null
}

function paintShape(
  ctx: CanvasRenderingContext2D,
  s: Record<string, unknown>,
  layer: LayerDef,
  fill: string,
  stroke: string,
  lineWidth: number,
) {
  ctx.lineWidth = lineWidth
  ctx.fillStyle = fill
  ctx.strokeStyle = stroke
  if (layer.type === 'polygon') {
    const pts = s.points as Array<{ x: number; y: number }> | undefined
    if (!pts?.length) return
    ctx.beginPath()
    ctx.moveTo(pts[0].x, pts[0].y)
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y)
    ctx.closePath()
    ctx.fill()
    ctx.stroke()
  } else {
    const x1 = s.x1 as number, y1 = s.y1 as number
    const x2 = s.x2 as number, y2 = s.y2 as number
    ctx.beginPath()
    ctx.rect(x1, y1, x2 - x1, y2 - y1)
    ctx.fill()
    ctx.stroke()
  }
}

function drawLegend(ctx: CanvasRenderingContext2D, lw: number, items: Array<{ color: string; label: string }>) {
  const fs   = Math.round(lw * 1.7)
  const pad  = Math.round(lw * 0.9)
  const sw   = Math.round(lw * 1.4)
  const rowH = fs + pad

  const boxW = fs * 11 + pad * 3 + sw
  const boxH = items.length * rowH + pad * 2

  ctx.fillStyle = 'rgba(0,0,0,0.62)'
  ctx.beginPath()
  ctx.rect(0, 0, boxW, boxH)
  ctx.fill()

  ctx.font = `600 ${fs}px sans-serif`
  ctx.textBaseline = 'middle'

  for (let i = 0; i < items.length; i++) {
    const ry = pad + i * rowH + rowH / 2
    ctx.fillStyle = items[i].color
    ctx.fillRect(pad, ry - sw / 2, sw, sw)
    ctx.fillStyle = '#fff'
    ctx.fillText(items[i].label, pad + sw + pad, ry)
  }
}

const OD_LEGEND = [
  { color: TP_COLORS.stroke, label: 'TP  True Positive'  },
  { color: FP_COLORS.stroke, label: 'FP  False Positive' },
  { color: FN_COLORS.stroke, label: 'FN  False Negative' },
]

const PR_LEGEND = [
  { color: CORRECT_COLORS.stroke, label: 'Correct barcode'   },
  { color: WRONG_COLORS.stroke,   label: 'Incorrect barcode' },
]

function drawShapes(
  canvas: HTMLCanvasElement,
  prInference: Record<string, unknown> | null,
  gtInference: Record<string, unknown> | null,
  assignment: Record<string, Record<string, string>> | null,
  visible: Set<string>,
  lineWidth: number,
  runType: string | null,
) {
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  if (runType === 'PR') {
    // PR mode: only assigned products/cartons, coloured by barcode agreement.
    for (const layer of PR_LAYERS) {
      if (!visible.has(layer.key)) continue
      const resolvedKey = resolveLayerKey(layer.key, gtInference)
      const prShapes = (prInference?.[resolvedKey] as unknown[]) ?? []
      const gtShapes = (gtInference?.[resolvedKey] as unknown[]) ?? []

      if (!assignment) continue

      const classMap = assignment[resolvedKey] ?? {}

      // Build uuid → shape map for GT so we can look up the matched GT shape
      const gtByUuid = new Map<string, Record<string, unknown>>()
      for (const shape of gtShapes) {
        const s = shape as Record<string, unknown>
        const uuid = s.uuid as string | undefined
        if (uuid) gtByUuid.set(uuid, s)
      }

      for (const shape of prShapes) {
        const s = shape as Record<string, unknown>
        const prUuid = s.uuid as string | undefined
        if (prUuid == null || !(prUuid in classMap)) continue  // unassigned — skip

        const gtShape = gtByUuid.get(classMap[prUuid])
        const prBarcode = getBarcode(s)
        const gtBarcode = gtShape ? getBarcode(gtShape) : null
        const correct = prBarcode != null && gtBarcode != null && prBarcode === gtBarcode
        const c = correct ? CORRECT_COLORS : WRONG_COLORS
        paintShape(ctx, s, layer, c.fill, c.stroke, lineWidth)
      }
    }
    drawLegend(ctx, lineWidth, PR_LEGEND)
    return
  }

  // OD mode (default)
  for (const layer of LAYERS) {
    if (!visible.has(layer.key)) continue

    // GT governs which actual array key to use (template_boxes vs template_boxes_up).
    // The same resolved key is used for PR shapes, GT shapes, and the assignment lookup
    // so that all three are in sync.
    const resolvedKey = resolveLayerKey(layer.key, gtInference)
    const prShapes = (prInference?.[resolvedKey] as unknown[]) ?? []
    const gtShapes = (gtInference?.[resolvedKey] as unknown[]) ?? []

    if (!assignment) {
      // No assignment data — draw all PR shapes in layer colour
      for (const shape of prShapes) {
        paintShape(ctx, shape as Record<string, unknown>, layer, layer.fill, layer.stroke, lineWidth)
      }
      continue
    }

    const classMap = assignment[resolvedKey] ?? {}
    const assignedGtUuids = new Set(Object.values(classMap))

    // TP / FP from PR shapes
    for (const shape of prShapes) {
      const s = shape as Record<string, unknown>
      const uuid = s.uuid as string | undefined
      const isTP = uuid != null && uuid in classMap
      const c = isTP ? TP_COLORS : FP_COLORS
      paintShape(ctx, s, layer, c.fill, c.stroke, lineWidth)
    }

    // FN from GT shapes not covered by any assignment
    for (const shape of gtShapes) {
      const s = shape as Record<string, unknown>
      const uuid = s.uuid as string | undefined
      if (uuid == null || assignedGtUuids.has(uuid)) continue
      paintShape(ctx, s, layer, FN_COLORS.fill, FN_COLORS.stroke, lineWidth)
    }
  }

  if (assignment) drawLegend(ctx, lineWidth, OD_LEGEND)
}


// ── Hit testing ───────────────────────────────────────────────────────────────

function pointInPolygon(x: number, y: number, pts: Array<{ x: number; y: number }>): boolean {
  let inside = false
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i].x, yi = pts[i].y
    const xj = pts[j].x, yj = pts[j].y
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

function pointInBBox(x: number, y: number, s: Record<string, unknown>): boolean {
  return x >= (s.x1 as number) && x <= (s.x2 as number) && y >= (s.y1 as number) && y <= (s.y2 as number)
}


// ── Raw shape adaptation ──────────────────────────────────────────────────────

function adaptVizSimCandidate(raw: Record<string, unknown>): VizSimCandidate {
  return {
    normalizedBarcode: (raw.normalized_barcode as string) ?? '',
    similarity: raw.similarity as number | null,
    isConfident: (raw.is_confident as boolean) ?? false,
    productName: raw.product_name as string | undefined,
    productImageUri: raw.product_image_uri as string | undefined,
    itemNumber: (raw.item_number as string | null | undefined) ?? undefined,
    productId: (raw.product_id as number | null | undefined) ?? undefined,
    referenceCrops: (raw.reference_crops as Array<Record<string, unknown>> | undefined)?.map((rc) => ({
      embeddingCropId: rc.embedding_crop_id as string,
      similarity: rc.similarity as number,
    })) as VizSimReferenceCrop[] | undefined,
    allNormalizedBarcodes: raw.all_normalized_barcodes as string[] | undefined,
  }
}

function adaptRawShape(raw: Record<string, unknown>): ProductRecognitionPolygon | ProductRecognitionBBox {
  const vizSimCandidates = (raw.viz_sim_candidates as Array<Record<string, unknown>> | undefined)?.map(adaptVizSimCandidate)
  const base = { confidence: raw.confidence as number | undefined, vizSimCandidates }
  if (Array.isArray(raw.points) && raw.points.length > 0) {
    const points = raw.points as Array<{ x: number; y: number }>
    const xs = points.map((p) => p.x)
    const ys = points.map((p) => p.y)
    const minX = Math.min(...xs), minY = Math.min(...ys)
    const maxX = Math.max(...xs), maxY = Math.max(...ys)
    return { ...base, points, shape: [minX, minY, maxX - minX, maxY - minY] as [number, number, number, number] } as ProductRecognitionPolygon
  }
  return { ...base, x1: raw.x1 as number, y1: raw.y1 as number, x2: raw.x2 as number, y2: raw.y2 as number } as ProductRecognitionBBox
}


// ── Image viewer with zoom/pan ────────────────────────────────────────────────

const ICON_BTN: React.CSSProperties = {
  background: 'rgba(0,0,0,0.55)',
  border: '1px solid rgba(255,255,255,0.15)',
  color: '#fff',
  borderRadius: 6,
  padding: '4px 8px',
  fontSize: 12,
  cursor: 'pointer',
  lineHeight: 1,
  backdropFilter: 'blur(4px)',
  userSelect: 'none',
}

interface ImageViewerProps {
  url: string | null | undefined
  prInference: Record<string, unknown> | null
  gtInference: Record<string, unknown> | null
  assignment: Record<string, Record<string, string>> | null
  visible: Set<string>
  runType: string | null
  maximized?: boolean
  onMaximize?: () => void
  onClose?: () => void
  onShapeRightClick?: (
    prShape: Record<string, unknown>,
    gtShape: Record<string, unknown> | null,
    layerKey: string,
    screenX: number,
    screenY: number,
  ) => void
}

function ImageViewer({ url, prInference, gtInference, assignment, visible, runType, maximized = false, onMaximize, onClose, onShapeRightClick }: ImageViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // Zoom/pan state — keep in refs to avoid stale closures in event handlers
  const scaleRef = useRef(1)
  const offsetRef = useRef({ x: 0, y: 0 })
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 })
  scaleRef.current = transform.scale
  offsetRef.current = { x: transform.x, y: transform.y }

  const panRef = useRef<{ startX: number; startY: number; ox: number; oy: number } | null>(null)
  const [panning, setPanning] = useState(false)

  // Reset transform when url changes or maximized toggles
  useEffect(() => { setTransform({ scale: 1, x: 0, y: 0 }) }, [url, maximized])

  // Canvas: set natural dimensions on image load
  const syncCanvas = useCallback(() => {
    const img = imgRef.current
    const canvas = canvasRef.current
    if (!img || !canvas || !img.naturalWidth) return
    if (canvas.width !== img.naturalWidth || canvas.height !== img.naturalHeight) {
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
    }
    // Scale lineWidth so it appears ~3px visually regardless of image resolution.
    // ratio = natural px per display px; e.g. 4000px image at 400px display → ratio=10 → lineWidth=30 → 3px on screen.
    const ratio = img.clientWidth > 0 ? img.naturalWidth / img.clientWidth : 1
    drawShapes(canvas, prInference, gtInference, assignment, visible, Math.max(1, 3 * ratio), runType)
  }, [prInference, gtInference, assignment, visible, runType])

  // Redraw every render — cheap clearRect + draw
  useEffect(() => { syncCanvas() })

  // Wheel zoom — passive: false so we can preventDefault
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault()
    const el = containerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const mx = e.clientX - rect.left
    const my = e.clientY - rect.top
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15
    const prev = scaleRef.current
    const next = Math.max(0.25, Math.min(20, prev * factor))
    const ox = offsetRef.current.x
    const oy = offsetRef.current.y
    setTransform({ scale: next, x: mx - (mx - ox) * (next / prev), y: my - (my - oy) * (next / prev) })
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    el.addEventListener('wheel', handleWheel, { passive: false })
    return () => el.removeEventListener('wheel', handleWheel)
  }, [handleWheel])

  // Drag to pan
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return
    e.preventDefault()
    panRef.current = { startX: e.clientX, startY: e.clientY, ox: offsetRef.current.x, oy: offsetRef.current.y }
    setPanning(true)
  }

  useEffect(() => {
    if (!panning) return
    const onMove = (e: MouseEvent) => {
      const pan = panRef.current
      if (!pan) return
      setTransform(prev => ({
        ...prev,
        x: pan.ox + e.clientX - pan.startX,
        y: pan.oy + e.clientY - pan.startY,
      }))
    }
    const onUp = () => { panRef.current = null; setPanning(false) }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp) }
  }, [panning])

  const resetZoom = (e: React.MouseEvent) => {
    e.stopPropagation()
    setTransform({ scale: 1, x: 0, y: 0 })
  }

  // Right-click: hit-test visible PR shapes and fire callback
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    if (!onShapeRightClick || runType !== 'PR') return
    e.preventDefault()
    const img = imgRef.current
    const container = containerRef.current
    if (!img || !container || !img.naturalWidth) return
    const rect = container.getBoundingClientRect()
    const cx = e.clientX - rect.left
    const cy = e.clientY - rect.top
    // Undo CSS transform → display coords → natural image pixels
    const ix = (cx - offsetRef.current.x) / scaleRef.current
    const iy = (cy - offsetRef.current.y) / scaleRef.current
    const nx = ix * (img.naturalWidth / img.clientWidth)
    const ny = iy * (img.naturalHeight / img.clientHeight)
    if (nx < 0 || ny < 0 || nx > img.naturalWidth || ny > img.naturalHeight) return
    for (const layer of PR_LAYERS) {
      if (!visible.has(layer.key)) continue
      const resolvedKey = resolveLayerKey(layer.key, gtInference)
      const prShapes = (prInference?.[resolvedKey] as unknown[]) ?? []
      for (const shape of prShapes) {
        const s = shape as Record<string, unknown>
        const pts = s.points as Array<{ x: number; y: number }> | undefined
        const hit = layer.type === 'polygon'
          ? (pts?.length ? pointInPolygon(nx, ny, pts) : false)
          : pointInBBox(nx, ny, s)
        if (!hit) continue
        const classMap = (assignment ?? {})[resolvedKey] ?? {}
        const prUuid = s.uuid as string | undefined
        let gtShape: Record<string, unknown> | null = null
        if (prUuid && prUuid in classMap) {
          const gtShapes = (gtInference?.[resolvedKey] as unknown[]) ?? []
          const found = gtShapes.find((g) => (g as Record<string, unknown>).uuid === classMap[prUuid])
          if (found) gtShape = found as Record<string, unknown>
        }
        onShapeRightClick(s, gtShape, resolvedKey, e.clientX, e.clientY)
        return
      }
    }
  }, [onShapeRightClick, runType, visible, prInference, gtInference, assignment])

  const isTransformed = transform.scale !== 1 || transform.x !== 0 || transform.y !== 0

  const noImage = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 260, color: 'var(--text-secondary)', fontSize: 12 }}>
      No image available
    </div>
  )

  return (
    <div
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onDoubleClick={resetZoom}
      onContextMenu={handleContextMenu}
      style={{
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid var(--border)',
        borderRadius: 8,
        background: 'var(--surface-raised)',
        cursor: panning ? 'grabbing' : (transform.scale > 1 ? 'grab' : 'default'),
        userSelect: 'none',
        height: maximized ? 'calc(100vh - 180px)' : undefined,
        minHeight: 260,
      }}
    >
      {!url ? noImage : (
        <div
          style={{
            transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
            transformOrigin: '0 0',
            width: '100%',
          }}
        >
          <img
            ref={imgRef}
            src={url}
            alt="Sample"
            onLoad={syncCanvas}
            draggable={false}
            style={{ width: '100%', height: 'auto', display: 'block', pointerEvents: 'none' }}
          />
          <canvas
            ref={canvasRef}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
          />
        </div>
      )}

      {/* Overlay controls — top-right corner */}
      <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 6, zIndex: 10 }} onMouseDown={e => e.stopPropagation()}>
        {isTransformed && (
          <button style={ICON_BTN} onClick={resetZoom} title="Reset zoom (double-click)">
            1:1
          </button>
        )}
        {onMaximize && (
          <button style={ICON_BTN} onClick={(e) => { e.stopPropagation(); onMaximize() }} title="Maximize">
            ⛶
          </button>
        )}
        {onClose && (
          <button style={ICON_BTN} onClick={(e) => { e.stopPropagation(); onClose() }} title="Close">
            ✕
          </button>
        )}
      </div>
    </div>
  )
}


// ── Layer toggle pills ────────────────────────────────────────────────────────

interface LayerControlsProps {
  layers: LayerDef[]
  prInference: Record<string, unknown> | null
  visible: Set<string>
  onChange: (key: string, checked: boolean) => void
}

function LayerControls({ layers, prInference, visible, onChange }: LayerControlsProps) {
  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 6,
      marginTop: 8,
      padding: '8px 10px',
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 8,
    }}>
      {layers.map((layer) => {
        const count = shapeCount(prInference, layer.key)
        const active = visible.has(layer.key) && count > 0
        const disabled = count === 0
        return (
          <button
            key={layer.key}
            disabled={disabled}
            onClick={() => !disabled && onChange(layer.key, !visible.has(layer.key))}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '3px 10px 3px 7px',
              borderRadius: 100,
              border: `1px solid ${active ? layer.stroke : 'var(--border)'}`,
              background: active ? layer.fill : 'transparent',
              color: active ? layer.stroke : 'var(--text-tertiary)',
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled ? 0.3 : 1,
              fontSize: 11,
              fontWeight: active ? 600 : 400,
              fontFamily: 'var(--font-body)',
              transition: 'all 120ms ease',
              userSelect: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{
              width: 8,
              height: 8,
              borderRadius: 2,
              flexShrink: 0,
              background: active ? layer.stroke : 'var(--border)',
              transition: 'background 120ms ease',
            }} />
            {layer.label}
            {count > 0 && (
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, opacity: 0.65, marginLeft: 1 }}>
                {count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}


// ── Maximize overlay ──────────────────────────────────────────────────────────

interface MaximizedOverlayProps {
  url: string | null | undefined
  prInference: Record<string, unknown> | null
  gtInference: Record<string, unknown> | null
  assignment: Record<string, Record<string, string>> | null
  visible: Set<string>
  runType: string | null
  layers: LayerDef[]
  onToggle: (key: string, checked: boolean) => void
  onClose: () => void
  onShapeRightClick?: ImageViewerProps['onShapeRightClick']
}

function MaximizedOverlay({ url, prInference, gtInference, assignment, visible, runType, layers, onToggle, onClose, onShapeRightClick }: MaximizedOverlayProps) {
  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0,0,0,0.88)',
        display: 'flex',
        flexDirection: 'column',
        padding: 16,
        gap: 10,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <ImageViewer
        url={url}
        prInference={prInference}
        gtInference={gtInference}
        assignment={assignment}
        visible={visible}
        runType={runType}
        maximized
        onClose={onClose}
        onShapeRightClick={onShapeRightClick}
      />
      <div style={{ flexShrink: 0 }}>
        <LayerControls layers={layers} prInference={prInference} visible={visible} onChange={onToggle} />
      </div>
    </div>
  )
}


// ── PR comparison panel ───────────────────────────────────────────────────────

interface RightClickState {
  prShape: Record<string, unknown>
  gtShape: Record<string, unknown> | null
  layerKey: string
  screenX: number
  screenY: number
}

interface PrComparisonPanelProps {
  state: RightClickState
  cameraImage: HTMLImageElement | null
  onClose: () => void
}

function DetectionPanel({ label, item, raw, cameraImage, color }: {
  label: string
  item: ProductRecognitionPolygon | ProductRecognitionBBox | null
  raw: Record<string, unknown> | null
  cameraImage: HTMLImageElement | null
  color: string
}) {
  return (
    <div className="flex flex-col">
      <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{label}</div>
      <div className="flex-1 overflow-y-auto">
        {item ? (
          <>
            <ProductRecognitionDetectionContent
              item={item}
              cameraImageElement={cameraImage ?? undefined}
              layerBboxColor={color}
              withReferenceCrops
            />
            <div className="mt-6 pt-4 border-t border-gray-200">
              <span className="text-gray-600 font-medium block mb-2 text-sm">Raw Data:</span>
              <pre className="text-xs bg-gray-50 p-3 rounded overflow-x-auto max-h-48 overflow-y-auto">
                {JSON.stringify(raw, null, 2)}
              </pre>
            </div>
          </>
        ) : (
          <div className="text-gray-400 text-sm p-4">No GT match for this shape.</div>
        )}
      </div>
    </div>
  )
}

function PrComparisonPanel({ state, cameraImage, onClose }: PrComparisonPanelProps) {
  const { prShape, gtShape } = state
  const prItem = useMemo(() => adaptRawShape(prShape), [prShape])
  const gtItem = useMemo(() => (gtShape ? adaptRawShape(gtShape) : null), [gtShape])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0 !mb-0"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-hidden flex flex-col max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">Detection Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Side-by-side panels */}
        <div className="grid grid-cols-2 divide-x divide-gray-200 overflow-y-auto">
          <div className="p-6">
            <DetectionPanel label="GT" item={gtItem} raw={gtShape} cameraImage={cameraImage} color="rgba(59,130,246,0.9)" />
          </div>
          <div className="p-6">
            <DetectionPanel label="Run" item={prItem} raw={prShape} cameraImage={cameraImage} color="rgba(251,146,60,0.9)" />
          </div>
        </div>
      </div>
    </div>
  )
}


// ── Page ──────────────────────────────────────────────────────────────────────

export const RunInferenceSamplePage: React.FC = () => {
  const { runId, runInferenceId } = useParams<{ runId: string; runInferenceId: string }>()
  const [searchParams] = useSearchParams()
  const { token } = useAuthContext()
  const [detail, setDetail] = useState<RunInferenceDetail | null>(null)
  const [run, setRun] = useState<EvalRun | null>(null)
  const [gtSample, setGtSample] = useState<EvalGtSample | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [visible, setVisible] = useState<Set<string>>(new Set())
  const [maximized, setMaximized] = useState(false)
  const [rightClick, setRightClick] = useState<RightClickState | null>(null)
  const [cameraImg, setCameraImg] = useState<HTMLImageElement | null>(null)

  useEffect(() => {
    if (!runId || !runInferenceId) return
    const gtId = searchParams.get('gt')
    Promise.all([
      EvalApiService.getRunInference(token ?? '', runId, runInferenceId),
      EvalApiService.getRun(token ?? '', runId),
      gtId ? EvalApiService.getGtSample(token ?? '', gtId) : Promise.resolve(null),
    ]).then(([d, r, gt]) => {
      if (!d) { setNotFound(true); setLoading(false); return }
      setDetail(d)
      setRun(r)
      if (gt) {
        setGtSample(gt)
      } else {
        EvalApiService.getGtSample(token ?? '', d.gt_sample.gt_sample_id).then(setGtSample)
      }
      setLoading(false)
    })
  }, [runId, runInferenceId, token, searchParams])

  // Compute imageUrl before hooks (detail may be null before load)
  const imageUrl = detail ? (gtSample?.image_url ?? detail.gt_sample?.image_url) : undefined

  // Load camera image element for crop visualisation in the comparison panel
  useEffect(() => {
    if (!imageUrl) return
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => setCameraImg(img)
    img.src = imageUrl
  }, [imageUrl])

  const handleShapeRightClick = useCallback((
    prShape: Record<string, unknown>,
    gtShape: Record<string, unknown> | null,
    layerKey: string,
    screenX: number,
    screenY: number,
  ) => {
    setRightClick({ prShape, gtShape, layerKey, screenX, screenY })
  }, [])

  if (loading) return <div className="page-content"><div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div></div>
  if (notFound || !detail) return <div className="page-content"><div style={{ color: 'var(--danger)', fontSize: 13 }}>Sample not found.</div></div>

  const prInference = detail.pr_inference as Record<string, unknown> | null
  const gtInferencePr = (gtSample?.pr_inference ?? detail.gt_sample?.pr_inference) as Record<string, unknown> | null
  const assignment = detail.run_to_gt_assignment
  const runType = run?.run_type ?? null
  const layers = runType === 'PR' ? PR_LAYERS : LAYERS

  const handleToggle = (key: string, checked: boolean) => {
    setVisible(prev => {
      const next = new Set(prev)
      checked ? next.add(key) : next.delete(key)
      return next
    })
  }

  return (
    <div className="page-content">
      {maximized && (
        <MaximizedOverlay
          url={imageUrl}
          prInference={prInference}
          gtInference={gtInferencePr}
          assignment={assignment}
          visible={visible}
          runType={runType}
          layers={layers}
          onToggle={handleToggle}
          onClose={() => setMaximized(false)}
          onShapeRightClick={runType === 'PR' ? handleShapeRightClick : undefined}
        />
      )}

      {rightClick && runType === 'PR' && (
        <PrComparisonPanel
          state={rightClick}
          cameraImage={cameraImg}
          onClose={() => setRightClick(null)}
        />
      )}

      <div className="eval-page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Link to={`/eval/runs/${runId}`} style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              ← Run {runId}
            </Link>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="eval-page-title" style={{ fontFamily: 'var(--font-mono)', fontSize: 15 }}>
              {runInferenceId}
            </div>
            {run && <RunTypeBadge runType={run.run_type} />}
          </div>
          <div className="eval-page-sub">
            GT sample{' '}
            <Link to={`/eval/gt-samples/${detail.gt_sample.gt_sample_id}`} style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
              {detail.gt_sample.gt_sample_id}
            </Link>
            {detail.gt_sample?.ingested_at && (
              <span style={{ marginLeft: 8 }}>· {fmtIso(detail.gt_sample.ingested_at)}</span>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) minmax(300px, 2fr)', gap: 20, marginBottom: 24, alignItems: 'start' }}>
        <div>
          <ImageViewer
            url={imageUrl}
            prInference={prInference}
            gtInference={gtInferencePr}
            assignment={assignment}
            visible={visible}
            runType={runType}
            onMaximize={() => setMaximized(true)}
            onShapeRightClick={runType === 'PR' ? handleShapeRightClick : undefined}
          />
          <LayerControls layers={layers} prInference={prInference} visible={visible} onChange={handleToggle} />
          {runType === 'PR' && (
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-tertiary)', textAlign: 'center' }}>
              Hint: right-click on a polygon for details
            </div>
          )}
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12 }}>
            Sample Metrics
          </div>
          <MetricsSummary result={detail.metrics} runType={run?.run_type ?? 'Full'} />
        </div>
      </div>

      {detail.gt_sample && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
          {detail.gt_sample.store_id != null && (
            <div className="eval-info-card"><div className="eval-info-label">Store</div><div className="eval-info-value">{detail.gt_sample.store_id}</div></div>
          )}
          {detail.gt_sample.chain_id != null && (
            <div className="eval-info-card"><div className="eval-info-label">Chain</div><div className="eval-info-value">{detail.gt_sample.chain_id}</div></div>
          )}
          {detail.gt_sample.image_id != null && (
            <div className="eval-info-card"><div className="eval-info-label">Image ID</div><div className="eval-info-value" style={{ fontFamily: 'var(--font-mono)' }}>{detail.gt_sample.image_id}</div></div>
          )}
          {detail.gt_sample.ingested_by && (
            <div className="eval-info-card"><div className="eval-info-label">Ingested By</div><div className="eval-info-value">{detail.gt_sample.ingested_by}</div></div>
          )}
        </div>
      )}
    </div>
  )
}
